import { z } from "zod";
import { producerStandingSchema } from "./producer.js";
/**
 * The quality report — every wine the cellar holds, judged from its producer
 * and cuvée, and a proposed standing for every producer behind them.
 *
 * The decision is in `project-scope.md`, dated 2026-09-27. It is asked, never
 * generated; it is kept, as a drinking-window suggestion is kept, and the list
 * of what was asked is its history; and a producer's standing moves only when
 * the owner accepts the proposal. It may be asked once an hour per cellar.
 *
 * **Wines are named by what the report froze, not by the wine's row today.**
 * A report is a document about the cellar on a day, so the number, producer,
 * name and vintage travel with it; `wineId` is there so the page can link to
 * the wine while it still exists, and is null once it does not.
 */
/** Four steps and a word each, the lowest of which is not a verdict. */
export const qualityTierSchema = z.enum(["Top", "VeryGood", "Good", "Everyday"]);
/**
 * What a wine's tier chiefly rests on: the house's reputation, the wine's
 * place in the house's range, or — the house being unknown to the model — the
 * appellation and the label alone.
 *
 * **The kind of knowledge, not a source.** The model looks nothing up; it has
 * no documents to cite, and the page says so beside this rather than letting
 * a basis read as a reference. The reasoning is on `QualityBasis` in the
 * Prisma schema.
 */
export const qualityBasisSchema = z.enum(["Producer", "Cuvee", "Appellation"]);
export const qualityReportWineSchema = z.object({
    wineId: z.string().nullable(),
    number: z.number().int(),
    producer: z.string(),
    name: z.string(),
    vintage: z.number().int().nullable(),
    isNonVintage: z.boolean(),
    tier: qualityTierSchema,
    basis: qualityBasisSchema,
    reason: z.string(),
});
/**
 * One producer's proposed standing.
 *
 * `current` is the producer's standing now, beside the two the report froze —
 * so the page can say *already so* without a second request, and so a
 * proposal the owner has since overruled by hand reads as that rather than as
 * one waiting to be taken. Null once the producer row is gone.
 */
export const qualityReportProducerSchema = z.object({
    id: z.string(),
    producerId: z.string().nullable(),
    name: z.string(),
    standing: producerStandingSchema,
    priorStanding: producerStandingSchema,
    current: producerStandingSchema.nullable(),
    acceptedAt: z.string().nullable(),
});
export const qualityReportSchema = z.object({
    id: z.string(),
    askedAt: z.string(),
    askedBy: z.string().nullable(),
    model: z.string(),
    /** How many wines were asked about. More than `wines.length` when the model
     *  left one out, which the page says rather than hides. */
    winesAsked: z.number().int(),
    /** Ordered best tier first, and by cellar number within a tier. */
    wines: z.array(qualityReportWineSchema),
    /** Ordered by name. */
    producers: z.array(qualityReportProducerSchema),
});
/** One line of the history. */
export const qualityReportSummarySchema = z.object({
    id: z.string(),
    askedAt: z.string(),
    askedBy: z.string().nullable(),
    model: z.string(),
    wineCount: z.number().int(),
    acceptedCount: z.number().int(),
});
/**
 * The history, newest first, and when the report may next be asked.
 *
 * `nextAllowedAt` is null when it may be asked now. It is the server's answer
 * rather than arithmetic in the browser on the newest row, because the hour is
 * also held by a question that is out right now and by one that died in
 * flight, neither of which is listed.
 */
export const qualityReportListResponseSchema = z.object({
    reports: z.array(qualityReportSummarySchema),
    nextAllowedAt: z.string().nullable(),
});
/** Accepting proposed standings — one, or every one on the page at once. */
export const acceptQualityStandingsRequestSchema = z.object({
    producerRowIds: z.array(z.string()).min(1).max(1000),
});
//# sourceMappingURL=quality.js.map